

import BottomNav from '../../../components/layout/BottomNav';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../utils/api';
import toast from 'react-hot-toast';
import BackButton from '../../../components/common/BackButton';

const G = '#1A6B3C';
const LIGHT = '#EAF3DE';
const STOCK_STYLE = {
  available:    { bg:'#DCFCE7', color:'#166534', label:'In stock' },
  low_stock:    { bg:'#FEF9C3', color:'#854D0E', label:'Low Stock' },
  out_of_stock: { bg:'#FEE2E2', color:'#991B1B', label:'Out of Stock' },
};

export default function NurseryDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [nursery, setNursery] = useState(null);
  const [plants,  setPlants]  = useState({});    // grouped by category
  const [allPlantsFlat, setAllPlantsFlat] = useState([]); // flat list
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('All');
  const [error,   setError]   = useState(null);
  const [cart,    setCart]    = useState(() => {
    try { return JSON.parse(localStorage.getItem('nursery_cart') || '[]'); } catch { return []; }
  });

  // Save cart to localStorage whenever it changes
  const saveCart = (newCart) => {
    setCart(newCart);
    localStorage.setItem('nursery_cart', JSON.stringify(newCart));
  };

  const addToCart = (plant) => {
    const existing = cart.find(c => c._id === plant._id);
    let newCart;
    if (existing) {
      newCart = cart.map(c => c._id === plant._id ? { ...c, qty: c.qty + 1 } : c);
    } else {
      newCart = [...cart, {
        _id:        plant._id,
        name:       plant.name,
        price:      plant.price,
        priceUnit:  plant.priceUnit || 'plant',
        image:      plant.images?.[0] || '',
        nurseryId:  id,
        nurseryName: nursery?.name || '',
        qty:        1,
      }];
    }
    saveCart(newCart);
    toast.success(`${plant.name} added to cart 🛒`);
  };

  const getQty = (plantId) => cart.find(c => c._id === plantId)?.qty || 0;
  const cartCount = cart.reduce((sum, c) => sum + c.qty, 0);

  useEffect(() => { load(); }, [id]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load nursery details
      const nRes = await api.get(`/nursery/${id}`);
      const nurseryData = nRes.data.nursery;
      setNursery(nurseryData);

      // Plants are embedded in nursery OR from separate endpoint
      if (nurseryData.plants?.length > 0) {
        // Plants are embedded in nursery document
        const grouped = {};
        nurseryData.plants
          .filter(p => p.isActive !== false)
          .forEach(plant => {
            const cat = plant.category || 'other';
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(plant);
          });
        setPlants(grouped);
        setAllPlantsFlat(nurseryData.plants.filter(p => p.isActive !== false));
      } else {
        // Try separate plants endpoint
        try {
          const pRes = await api.get(`/nursery/${id}/plants`);
          const plantsData = pRes.data.plants || {};
          setPlants(plantsData);
          setAllPlantsFlat(Object.values(plantsData).flat());
        } catch {
          setPlants({});
          setAllPlantsFlat([]);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load nursery');
      setNursery(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'60vh', flexDirection:'column', gap:12 }}>
      <div className="spinner" style={{ width:36, height:36 }} />
      <div style={{ fontSize:13, color:'#9CA3AF' }}>Loading nursery...</div>
    </div>
  );

  if (error || !nursery) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'60vh', flexDirection:'column', gap:12, color:'#9CA3AF' }}>
      <div style={{ fontSize:48 }}></div>
      <div style={{ fontSize:14 }}>{error || 'Nursery not found'}</div>
      <button onClick={() => navigate(-1)}
        style={{ background:G, color:'white', border:'none', borderRadius:20, padding:'10px 24px', fontSize:13, cursor:'pointer' }}>
        Go Back
      </button>
    </div>
  );

  const cats      = ['All', ...Object.keys(plants)];
  const shown     = filter === 'All' ? allPlantsFlat : (plants[filter] || []);
  const totalPlants = allPlantsFlat.length;

  return (
    <div style={{ background:'#F9FBF7', minHeight:'100vh', paddingBottom:90, fontFamily:'Inter,system-ui,sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ background:G, padding:'14px 12px 14px', color:'white', position:'relative' }}>
        <BackButton style={{ position:'absolute', top:14, left:12, zIndex:2 }} />

        <div style={{ display:'flex', gap:12, paddingLeft:46, marginTop:2 }}>
          {/* Nursery photo */}
          <div style={{ width:56, height:56, borderRadius:12, background:'rgba(255,255,255,.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, flexShrink:0, overflow:'hidden', border:'2px solid rgba(255,255,255,.3)' }}>
            {nursery.photo
              ? <img src={nursery.photo} alt={nursery.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              : ''}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:15, fontWeight:700, lineHeight:1.3, wordBreak:'break-word' }}>{nursery.name}</div>
            <div style={{ fontSize:11, opacity:.8, marginTop:3, lineHeight:1.3, wordBreak:'break-word' }}>👤 {nursery.ownerName}</div>
            <div style={{ fontSize:11, opacity:.7, marginTop:2, lineHeight:1.4, wordBreak:'break-word' }}> {nursery.address || nursery.city}</div>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display:'flex', gap:8, marginTop:12, paddingLeft:4 }}>
          {[
            { value: totalPlants, label:'Plants' },
            { value: nursery.specialties?.length || 0, label:'Categories' },
            { value: nursery.timings?.split(',')[0] || '7am–7pm', label:'Timings' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign:'center', background:'rgba(255,255,255,.15)', borderRadius:8, padding:'5px 12px' }}>
              <div style={{ fontSize:13, fontWeight:700 }}>{s.value}</div>
              <div style={{ fontSize:9, opacity:.75 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {nursery.mobile && (
          <a href={`tel:+91${nursery.mobile}`}
            style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginTop:12, background:'rgba(255,255,255,.2)', border:'1px solid rgba(255,255,255,.3)', color:'white', borderRadius:10, padding:'10px 14px', fontSize:13, fontWeight:700, textDecoration:'none' }}>
            📞 Call Nursery
          </a>
        )}
      </div>

      {/* ── Category filter tabs ── */}
      <div style={{ display:'flex', gap:6, padding:'10px 12px', overflowX:'auto', scrollbarWidth:'none', background:'white', borderBottom:'1px solid #E8F5E9' }}>
        {cats.map(cat => (
          <button key={cat} onClick={() => setFilter(cat)}
            style={{ flexShrink:0, padding:'6px 14px', borderRadius:20, border:`1.5px solid ${filter===cat?G:'#E5E7EB'}`, background:filter===cat?G:'white', color:filter===cat?'white':'#757575', fontSize:11, fontWeight:filter===cat?700:400, cursor:'pointer', textTransform:'capitalize', whiteSpace:'nowrap' }}>
            {cat} {cat === 'All' ? `(${totalPlants})` : `(${plants[cat]?.length || 0})`}
          </button>
        ))}
      </div>

      <div style={{ padding:'12px 12px' }}>

        {/* Description */}
        {nursery.description && (
          <div style={{ background:'white', borderRadius:12, padding:'12px 14px', marginBottom:12, border:'1px solid #F1F8E9', fontSize:12, color:'#616161', lineHeight:1.6 }}>
            {nursery.description}
          </div>
        )}

        {/* Specialties */}
        {nursery.specialties?.length > 0 && (
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
            {nursery.specialties.map(s => (
              <span key={s} style={{ background:'#EAF3DE', color:G, fontSize:11, fontWeight:600, padding:'4px 10px', borderRadius:20, textTransform:'capitalize' }}>
                {s}
              </span>
            ))}
          </div>
        )}

        {/* Plants grid */}
        {shown.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px 0', color:'#9E9E9E' }}>
            <div style={{ fontSize:40, marginBottom:8 }}></div>
            <div style={{ fontSize:13 }}>
              {totalPlants === 0 ? 'No plants available yet' : 'No plants in this category'}
            </div>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            {shown.map(p => {
              const st = STOCK_STYLE[p.status] || STOCK_STYLE.available;
              return (
                <div key={p._id}
                  style={{ background:'white', borderRadius:14, overflow:'hidden', border:'0.5px solid #E5E7EB', cursor:'pointer' }}>
                  {/* Image */}
                  <div style={{ height:90, background:LIGHT, display:'flex', alignItems:'center', justifyContent:'center', position:'relative', overflow:'hidden' }}>
                    {p.images?.[0]
                      ? <img src={p.images[0]} alt={p.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                      : p.emoji ? <span style={{ fontSize:36 }}>{p.emoji}</span> : null}
                    <span style={{ position:'absolute', top:6, left:6, background:st.bg, color:st.color, fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:8 }}>{st.label}</span>
                  </div>
                  {/* Info */}
                  <div style={{ padding:'8px 10px 10px' }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'#1a2e05', marginBottom:2, lineHeight:1.3 }}>{p.name}</div>
                    {p.stage && <div style={{ fontSize:10, color:'#9CA3AF', marginBottom:3 }}>{p.stage}</div>}
                    {/* <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:4 }}>
                      <div>
                        <span style={{ fontSize:14, fontWeight:800, color:G }}>
                          {p.price ? `₹${p.price}` : 'Price TBD'}
                        </span>
                        {p.price && <span style={{ fontSize:9, color:'#9CA3AF' }}> /{p.priceUnit||'plant'}</span>}
                      </div>
                      {p.status !== 'out_of_stock' && (
                        getQty(p._id) === 0 ? (

                          <button
                            onClick={e => { e.stopPropagation(); addToCart(p); }}
                            style={{ background:G, color:'white', border:'none', borderRadius:16, padding:'5px 12px', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                            + Add
                          </button>

                          
                        ) : (
                          <div onClick={e => e.stopPropagation()}
                            style={{ display:'flex', alignItems:'center', gap:4, background:'#EAF3DE', borderRadius:16, padding:'3px 6px' }}>
                            <button
                              onClick={() => { const q = getQty(p._id); if(q<=1) saveCart(cart.filter(c=>c._id!==p._id)); else saveCart(cart.map(c=>c._id===p._id?{...c,qty:c.qty-1}:c)); }}
                              style={{ width:20, height:20, borderRadius:'50%', background:G, border:'none', color:'white', fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, lineHeight:1 }}>−</button>
                            <span style={{ fontSize:12, fontWeight:700, color:G, minWidth:16, textAlign:'center' }}>{getQty(p._id)}</span>
                            <button
                              onClick={() => addToCart(p)}
                              style={{ width:20, height:20, borderRadius:'50%', background:G, border:'none', color:'white', fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, lineHeight:1 }}>+</button>
                          </div>
                        )
                      )}
                    </div> */}
                    {p.quantity > 0 && (
                      <span style={{ fontSize:9, padding:'2px 6px', borderRadius:4, background:st.bg, color:st.color, fontWeight:600, display:'inline-block', marginTop:4 }}>
                        {p.quantity} available
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}